import * as yup from "yup";
import { useTranslation } from "react-i18next";

export const Appl30AmendmentStatutesValidationSchema = (
  submissionDate: Date,
) => {
  const { t } = useTranslation();
  const minDate = new Date("1900-01-01");

  return yup
    .object()
    .shape({
      coYearsToClose: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Διάρκεια εταιρείας μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        )
        .when("coAoristi", {
          is: true,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.nullable(),
        }),
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      coZipCode: yup
        .string()
        .max(
          12,
          t(
            "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
          ),
        )
        .nullable(),
      docPartnerDecisionDate: yup
        .date()
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900"))
        .max(
          submissionDate || new Date(),
          t(
            "Η ημερομηνία πρέπει να είναι πριν την ημερομηνία υποβολής της αίτησης",
          ),
        )
        .typeError(t("Η ημερομηνία πρέπει να είναι σε σωστή μορφή")),
      fudocStatute: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocStatuteSigned: yup
        .string()
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      docStatuteDate: yup
        .date()
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900"))
        .typeError(t("Η ημερομηνία πρέπει να είναι σε σωστή μορφή")),
      majority: yup
        .string()
        .max(255, t("Το πεδίο δεν πρέπει να υπερβαίνει τους 255 χαρακτήρες")),
      specialMajority: yup
        .string()
        .max(255, t("Το πεδίο δεν πρέπει να υπερβαίνει τους 255 χαρακτήρες")),
      changedArticles: yup.array().of(
        yup.object().shape({
          editedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      deletedArticles: yup.array().of(
        yup.object().shape({
          deletedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newArticles: yup.array().of(
        yup.object().shape({
          addedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newBoardMembers: yup.array().of(
        yup.object().shape({
          fp1Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp1Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp1SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp1JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Name: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Surname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Fathersname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Doy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Adt: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(20).required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Odos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Arithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Tk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Polh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2BirthCity: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(
                50,
                t(
                  "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                ),
              ),
          }),

          fp2BirthYear: yup
            .string()
            .transform((_, val) => (val !== "" ? val : null))
            .nullable()
            .notRequired()
            .when("ok", {
              is: "no",
              then: (schema) =>
                schema.test(
                  "four-digit-year",
                  t("Ελέγξτε την ορθότητα των στοιχείων."),
                  (value) =>
                    value === null ||
                    value === undefined ||
                    /^\d{4}$/.test(value),
                ),
            }),

          fp2Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          fp2JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
        }),
      ),
      newLegalEntityMembers: yup.array().of(
        yup.object().shape({
          partnerCompPercentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompIdiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompSoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompJointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompGemi2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompName2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompDoy2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompArithm2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompPolh2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompDhmos2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.required(t("Το πεδίο είναι υποχρεωτικό")).shape({
                label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
                value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
          }),

          partnerCompPercentage2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(12).required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompIdiot2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompSoloRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompJointlyRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          partnerCompTk2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          newRepresentatives: yup.array().of(
            yup.object().shape({
              fp2CompReprName: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprSurname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprFathersname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprDoy: yup.object().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprAdt: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprOdos: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprArithm: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprTk: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      12,
                      t(
                        "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprBirthCity: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprPolh: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),

              fp2CompReprBirthYear: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .required(t("Το πεδίο είναι υποχρεωτικό"))
                    .test(
                      "four-decimal-year",
                      t("Ελέγξτε την ορθότητα των στοιχείων."),
                      (value) =>
                        value === undefined ||
                        value === null ||
                        /^\d{4}?$/.test(value.toString()),
                    ),
              }),
            }),
          ),
        }),
      ),
      i21_capitalstock: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      i19_shareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      i19_nominalprice: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareNum: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      capital: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      shareValue: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
    })
    .test("capital-match", function (value) {
      const { shareNum, shareValue, capital } = value;
      if (shareNum || shareValue) {
        if (!shareNum) {
          return this.createError({
            message: t(
              "Πρέπει να συμπληρώσετε το Πλήθος Μεριδίων εφόσον έχει συμπληρωθεί η Ονομαστική Αξία Μεριδίου.",
            ),
          });
        }

        if (!shareValue) {
          return this.createError({
            message: t(
              "Πρέπει να συμπληρώσετε την Ονομαστική Αξία Μεριδίου εφόσον έχει συμπληρωθεί το Πλήθος Μεριδίων.",
            ),
          });
        }
      }
      if (capital && shareNum && shareValue) {
        if (
          parseFloat(capital || "0") !==
          parseFloat(shareNum || "0") * parseFloat(shareValue || "0")
        ) {
          return this.createError({
            message: t("Το Κεφάλαιο δεν ταιριάζει με τα στοιχεία μεριδίων."),
          });
        }
      }
      return true;
    })
    .test(
      "at-least-one-article",
      t("Πρέπει να εισάγετε αναλυτικά τις τροποποιήσεις του καταστατικού."),
      (value) =>
        (value.changedArticles && value.changedArticles.length > 0) ||
        (value.deletedArticles && value.deletedArticles.length > 0) ||
        (value.newArticles && value.newArticles.length > 0),
    );
};
